const localeFor = (language?: string) => language?.startsWith('fr') ? 'fr-CA' : 'en-CA';

export const formatMissionCurrency = (value: number | string | undefined, language?: string): string => {
  const numericValue = typeof value === 'number' ? value : Number(String(value ?? '0').replace(/\s/g, '').replace(',', '.'));
  const amount = new Intl.NumberFormat(localeFor(language), { maximumFractionDigits: 2 })
    .format(Number.isFinite(numericValue) ? numericValue : 0);
  return language?.startsWith('fr') ? `${amount}\u00a0$` : `$${amount}`;
};

const normalizeExampleAmount = (value: number, direction: 'down' | 'up') => {
  const safeValue = Number.isFinite(value) && value > 0 ? value : 0;
  if (safeValue < 20) return safeValue;
  const rounded = direction === 'up' ? Math.ceil(safeValue / 5) * 5 : Math.floor(safeValue / 5) * 5;
  return rounded > 0 ? rounded : safeValue;
};

// These are explanatory examples only. Actual reimbursement stays server-owned.
export const getMissionReimbursementHelp = (value: string, maximum: number | undefined, language?: string) => {
  const amount = (input: number) => new Intl.NumberFormat(localeFor(language), {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(Number.isFinite(input) ? input : 0);
  const match = value.replace(/\s/g, '').replace(/,/g, '.').match(/(\d+(?:\.\d+)?)/);
  const numeric = match ? Number(match[1]) : 0;
  const max = Number(maximum ?? 0);
  const fixed = value.includes('$');
  const total = Math.max(0, max > 0 ? max : numeric);
  if (fixed) {
    return {
      visible: total > 0,
      key: 'help.points-rate-dollars-new',
      fallbackKey: 'help.points-rate-dollars',
      values: { totalValue: amount(total), exceedingValue: amount(total + 5), underValue: amount(total > 15 ? total - 15 : total / 2) },
    };
  }

  const rate = numeric > 0 ? numeric / 100 : 0;
  const threshold = max > 0 && rate > 0 ? max / rate : 0;
  const lower = rate > 0 ? normalizeExampleAmount(max > 0 ? Math.min(100, threshold / 2) : 100, 'down') : 0;
  const higher = rate > 0 ? normalizeExampleAmount(max > 0 ? Math.max(threshold * 1.2, lower + 25) : 200, 'up') : 0;
  const cap = max > 0 ? max : Number.POSITIVE_INFINITY;
  return {
    visible: true,
    key: rate > 0 ? (max > 0 ? 'help.points-rate-percentage-new' : 'help.points-rate-percentage-no-max-new') : 'help.points-rate-percentage',
    fallbackKey: 'help.points-rate-percentage',
    values: {
      percentageValue: new Intl.NumberFormat(localeFor(language), { maximumFractionDigits: 2 }).format(numeric),
      maxAmount: amount(max),
      lowerPurchaseAmount: amount(lower), lowerReimbursementAmount: amount(rate > 0 ? Math.min(cap, lower * rate) : 0),
      higherPurchaseAmount: amount(higher), higherReimbursementAmount: amount(rate > 0 ? Math.min(cap, higher * rate) : 0),
    },
  };
};
